import React from 'react';
import { isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  getOrderedPageData,
  resolvePlaceholders,
} from 'routes/screens/response/utils';
import Icon from 'shared/components/Icon';
import {
  AccountInfoType,
  SurveyBlockCategoryType,
  SurveyBlockWithQuestionType,
  SurveyResponseDetailedDataType,
  SurveyResponseType,
  SurveyType,
} from 'shared/types';
import ResponsePageTitleAndDesc from './ResponsePageTitleAndDesc';
import ResponseCardBlock from './response-card-block';

type ResponseCardBlockHolderProps = {
  compactMode?: boolean;
  survey: SurveyType;
  response: SurveyResponseType;
  surveyBlocks?: SurveyBlockWithQuestionType[];
  ratingTypeId?: string;
  accountInfo?: AccountInfoType;
  placeholders: Record<string, string>;
  isResponseTranslated?: boolean;
  responseTranslation?: any;
  pageQuestion?: string;
  isLoadingResponseTranslation?: boolean;
};

const ResponseCardBlockHolder = (props: ResponseCardBlockHolderProps) => {
  const {
    compactMode,
    survey,
    response,
    surveyBlocks,
    ratingTypeId,
    accountInfo,
    placeholders,
    isResponseTranslated,
    responseTranslation,
    pageQuestion,
    isLoadingResponseTranslation,
  } = props;
  const { t } = useTranslation();
  const [showAll, toggleShowAll] = React.useState<boolean>(false);

  const { question_answers } = response;
  const page_detail = isResponseTranslated
    ? responseTranslation.page_data
    : response.page_data;

  const page_data = getOrderedPageData(page_detail, survey);

  const data = isResponseTranslated
    ? responseTranslation.detailed_data
    : response?.detailed_data;

  const detailedData = data
    .map((d) => {
      return {
        ...d,
        order: survey.blocks?.find((b) =>
          b.question_name
            ? b.question_name === d.name
            : b.category?.find((c) => c.question_name == d.name),
        )?.order,
      };
    })
    .sort((a, b) => {
      if (a?.order && b?.order) {
        return a.order - b.order;
      }
      return 0;
    });

  const getQuestionTitle = (block, questionAnswer, questionTitleText) => {
    if (block?.is_deleted && !isNil(questionAnswer)) {
      return questionTitleText;
    } else if (
      block?.deleted_on &&
      block?.is_deleted &&
      isNil(questionAnswer)
    ) {
      return '';
    } else if (!block?.is_deleted) {
      return questionTitleText;
    }
    return '';
  };

  function isDropdownOrMultiSelect(survey: SurveyType, question_answers: any) {
    const question = survey?.question_details?.questions?.find(
      (q) => q?.name === question_answers?.others_meta?.[q?.name],
    );
    const questionType = question?.type_info?.question_type?.toString();
    return (
      questionType === 'DROPDOWN' ||
      questionType === 'MULTI_SELECT' ||
      questionType === 'MULTI_SELECT_LOOKUP'
    );
  }

  const getResolvedQuestionAnswer = (
    block,
    question,
    respondedQuestionAnswer,
    respondedAnswer,
  ) => {
    const OTHERS_VALUE = `${t('Others')}`;
    const isAnswerArray = Array.isArray(respondedAnswer);
    if (isAnswerArray) {
      return respondedAnswer
        ?.map((a: any) => {
          return a;
        })
        .join(', ');
    } else if (
      question_answers?.others_meta?.[question?.name] &&
      isDropdownOrMultiSelect(survey, question_answers)
    ) {
      return question_answers?.others_meta[question?.name];
    } else if (
      question_answers?.others_meta &&
      isDropdownOrMultiSelect(survey, question_answers)
    ) {
      return (
        respondedAnswer
          ?.toString()
          .replace(
            OTHERS_VALUE,
            question_answers?.others_meta[question?.name] ?? `${t('others')}`,
          ) || (block?.deleted_on && !respondedAnswer ? '' : '-')
      );
    } else return respondedAnswer || '-';
  };

  const getBlocks = (question_ids: Array<string>) => {
    return detailedData?.length
      ? detailedData
          .map((respondedQuestionAnswer: SurveyResponseDetailedDataType) => {
            const block = surveyBlocks?.find((b: SurveyBlockWithQuestionType) =>
              b.question_name
                ? respondedQuestionAnswer.name === b.question_name
                : b.category?.find(
                    (item: SurveyBlockCategoryType) => item.id === ratingTypeId,
                  )?.question_name === respondedQuestionAnswer.name,
            );
            if (!block) return;
            const thisQuestion = question_ids?.length
              ? survey.question_details?.questions?.find(
                  (q) =>
                    q.name === respondedQuestionAnswer.name &&
                    question_ids.indexOf(q.name) > -1,
                )
              : survey.question_details?.questions?.find(
                  (q) => q.name === respondedQuestionAnswer.name,
                );
            const translatedQuestionData = response?.detailed_data?.find(
              (q) => q.name === respondedQuestionAnswer.name,
            );

            if (!thisQuestion) return;

            const question = {
              ...thisQuestion,
              type_info: {
                ...thisQuestion.type_info,
                scale_properties: {
                  ...thisQuestion.type_info?.scale_properties,
                  labels: {
                    ...thisQuestion.type_info?.scale_properties?.labels,
                    values: translatedQuestionData
                      ? translatedQuestionData?.labels
                      : thisQuestion.type_info?.scale_properties?.labels
                          ?.values,
                  },
                },
              },
            };

            const respondedAnswer =
              respondedQuestionAnswer?.answer !== undefined
                ? typeof respondedQuestionAnswer?.answer === 'boolean'
                  ? String(respondedQuestionAnswer?.answer)
                  : respondedQuestionAnswer?.answer
                : respondedQuestionAnswer?.labels?.length
                ? respondedQuestionAnswer?.labels[0]
                : question_answers[question?.name];

            const respondedQuestion: string =
              respondedQuestionAnswer?.question || '';
            const questionTitleText = respondedQuestionAnswer
              ? respondedQuestion
              : resolvePlaceholders({ text: respondedQuestion, placeholders });
            const questionTitle = getQuestionTitle(
              block,
              respondedAnswer,
              questionTitleText,
            );
            const resolvedQuestionAnswer = getResolvedQuestionAnswer(
              block,
              question,
              respondedQuestionAnswer,
              respondedAnswer,
            );

            if (questionTitle) {
              return {
                block,
                question,
                questionTitle,
                respondedQuestionAnswer,
                resolvedQuestionAnswer,
              };
            } else {
              return;
            }
          })
          .filter((val) => !!val)
      : [];
  };

  let responseBlocks: any[] = [];
  if (page_data && page_data?.length) {
    page_data.map((page: any) => {
      responseBlocks.push({ page, blocks: getBlocks(page?.question_ids) });
    });
  } else {
    responseBlocks = getBlocks([]);
  }

  const responseBlocksToRender = compactMode
    ? !showAll && responseBlocks.length
      ? [responseBlocks[0]]
      : responseBlocks
    : responseBlocks;

  return (
    <>
      {page_data && page_data?.length
        ? responseBlocksToRender.map((responseBlock, i: number) => {
            return (
              <div className="pages-card" key={i}>
                <ResponsePageTitleAndDesc page={responseBlock.page} />
                {responseBlock.blocks.map((block, index: number) => {
                  return (
                    block.respondedQuestionAnswer && (
                      <div className={`${block.block.name}-block`} key={index}>
                        <ResponseCardBlock
                          survey={survey}
                          block={block.block}
                          response={response}
                          question={block.question}
                          questionName={block.block.title}
                          questionTitle={block.questionTitle}
                          detailed_data={detailedData}
                          page_data={page_data}
                          accountInfo={accountInfo}
                          respondedQuestionAnswer={
                            block.respondedQuestionAnswer
                          }
                          resolvedQuestionAnswer={block.resolvedQuestionAnswer}
                          isFetchedTranslation={isLoadingResponseTranslation}
                        />
                      </div>
                    )
                  );
                })}
              </div>
            );
          })
        : responseBlocksToRender.map((responseBlock, index: number) => {
            return (
              responseBlock.respondedQuestionAnswer && (
                <div
                  className={`${responseBlock.block.name}-block`}
                  key={index}
                >
                  <ResponseCardBlock
                    survey={survey}
                    block={responseBlock.block}
                    response={response}
                    question={responseBlock.question}
                    questionName={responseBlock.block.title}
                    questionTitle={responseBlock.questionTitle}
                    detailed_data={detailedData}
                    page_data={page_data}
                    accountInfo={accountInfo}
                    respondedQuestionAnswer={
                      responseBlock.respondedQuestionAnswer
                    }
                    resolvedQuestionAnswer={
                      responseBlock.resolvedQuestionAnswer
                    }
                    isFetchedTranslation={isLoadingResponseTranslation}
                  />
                </div>
              )
            );
          })}
      {compactMode && responseBlocks.length > 1 && (
        <>
          <div className="more" onClick={() => toggleShowAll((old) => !old)}>
            {showAll
              ? t('hide')
              : t('responses.more', {
                  len: responseBlocks.length - 1,
                })}
          </div>
          <Link
            to={`/response/${response.survey_id}/${response.id}`}
            target="blank"
          >
            <div className="view-all">
              {t('view_all')}
              <Icon
                fill="#006cc2"
                width="12"
                height="12"
                glyph="open-new-tab"
              />
            </div>
          </Link>
        </>
      )}
    </>
  );
};

export default ResponseCardBlockHolder;
